import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HEADER_HEIGHT } from "./general/header/Header";
import DefaultButton from "./general/buttons/DefaultButton";
import {
    acceptInvite,
    fetchInvitePreview,
    InvitePreview,
    notifyPlaylistsChanged,
} from "../services/playlist";
import { useAuth } from "../services/auth";
import { PENDING_INVITE_KEY } from "../services/postAuthRedirect";
import { colors } from "../theme";

/**
 * Where an owner's share link lands. Deliberately outside RequireAuth: most people
 * opening an invite won't be signed in yet, and bouncing them to the landing page
 * would silently throw the invite away. The token is parked in sessionStorage so
 * logging in or signing up comes straight back here - see postAuthRedirect.
 */

export default function InvitePage() {
    const { token } = useParams<{ token: string }>();
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [preview, setPreview] = useState<InvitePreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) {
            return;
        }
        if (isLoggedIn) {
            sessionStorage.removeItem(PENDING_INVITE_KEY);
        } else {
            sessionStorage.setItem(PENDING_INVITE_KEY, token);
        }
    }, [token, isLoggedIn]);

    const load = useCallback(async () => {
        if (!token || !isLoggedIn) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setPreview(await fetchInvitePreview(token));
        } catch (e) {
            setError(e instanceof Error ? e.message : "This invite link is no longer valid");
        } finally {
            setLoading(false);
        }
    }, [token, isLoggedIn]);

    useEffect(() => {
        load();
    }, [load]);

    const handleJoin = async () => {
        if (!token) return;
        setJoining(true);
        setError(null);
        try {
            await acceptInvite(token);
            sessionStorage.removeItem(PENDING_INVITE_KEY);
            notifyPlaylistsChanged();
            navigate("/dashboard");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't join this playlist");
            setJoining(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: colors.sidebar }}>
            <Header />
            <Box
                sx={{
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 460,
                        p: 4,
                        borderRadius: 3,
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        textAlign: "center",
                    }}
                >
                    {!isLoggedIn && (
                        <>
                            <Typography variant="h5" sx={{ mb: 1 }}>You've been invited</Typography>
                            <Typography sx={{ color: colors.textSecondary, fontSize: 14 }}>
                                Log in or sign up to see this playlist and join it. We'll bring you
                                right back here.
                            </Typography>
                        </>
                    )}

                    {isLoggedIn && loading && <CircularProgress size={28} />}

                    {isLoggedIn && error && (
                        <>
                            <Typography variant="h5" sx={{ mb: 1 }}>Invite unavailable</Typography>
                            <Typography sx={{ color: colors.danger, fontSize: 14, mb: 2.5 }}>
                                {error}
                            </Typography>
                            <DefaultButton text="Go to your library" onClick={() => navigate("/dashboard")} />
                        </>
                    )}

                    {isLoggedIn && preview && !error && (
                        <>
                            <Typography sx={{ color: colors.textSecondary, fontSize: 13, mb: 0.5 }}>
                                {preview.ownerUsername || "Someone"} invited you to
                            </Typography>
                            <Typography variant="h4" sx={{ mb: 1 }}>{preview.playlistName}</Typography>
                            <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 3 }}>
                                {preview.songCount} {preview.songCount === 1 ? "song" : "songs"}
                            </Typography>

                            {preview.owned ? (
                                <>
                                    <Typography sx={{ color: colors.textSecondary, fontSize: 14, mb: 2.5 }}>
                                        This is your own playlist.
                                    </Typography>
                                    <DefaultButton text="Go to your library" onClick={() => navigate("/dashboard")} />
                                </>
                            ) : preview.contributor ? (
                                <>
                                    <Typography sx={{ color: colors.success, fontSize: 14, mb: 2.5 }}>
                                        You're already a contributor here.
                                    </Typography>
                                    <DefaultButton text="Open it" onClick={() => navigate("/dashboard")} />
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ color: colors.textSecondary, fontSize: 14, mb: 2.5 }}>
                                        Joining follows this playlist and lets you add songs to it.
                                    </Typography>
                                    <DefaultButton
                                        text={joining ? "Joining…" : "Join as contributor"}
                                        onClick={handleJoin}
                                        disabled={joining}
                                        size="large"
                                    />
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
